import { useTranslation } from 'react-i18next';
import { FileTextIcon } from 'lucide-react';

import { AnalysisSubHeader, BrandIdentity } from '../../../../components';
import type { CreativeLandscapeBrand } from '../../CreativeLandscape.types';
import { MicroLabel } from '../MicroLabel';

interface ExecutiveSummaryProps {
  /** One row per brand, already ordered and colour-keyed by the page. */
  brands: CreativeLandscapeBrand[];
  rows: Array<{ advertiser: string; persona: string }>;
  /** Scope and range line, e.g. "Cross-competitor insights · 6 brands · Sep 2025 – Nov 2025". */
  meta: string;
}

export const ExecutiveSummary = ({ brands, rows, meta }: ExecutiveSummaryProps) => {
  const { t } = useTranslation('analysis');
  const byAdvertiser = new Map(brands.map((brand) => [brand.name, brand]));
  // Alphabetical, as the design shows: the summary is a roster, not a ranking.
  const ordered = [...rows].sort((a, b) => a.advertiser.localeCompare(b.advertiser));

  return (
    <div className="flex flex-col gap-3xl">
      <AnalysisSubHeader
        title={t('analysisResult.creativeLandscape.aiOverview')}
        subtitle={meta}
        subtitleTone="meta"
      />
      <div className="flex flex-col gap-3.5">
        <MicroLabel icon={FileTextIcon} variant="eyebrow">
          {t('analysisResult.creativeLandscape.executiveSummary')}
        </MicroLabel>
        {/* A card per brand rather than a stacked list: the summaries are peers, and the
            grid lets a reader compare them side by side instead of scanning down. */}
        <ul className="grid grid-cols-1 items-stretch gap-sm md:grid-cols-2 lg:grid-cols-3">
          {ordered.map((row) => {
            const brand = byAdvertiser.get(row.advertiser);
            return (
              <li
                key={row.advertiser}
                className="flex h-full flex-col gap-3 rounded-lg border border-border-light p-xl"
              >
                <BrandIdentity
                  advertiser={row.advertiser}
                  domain={brand?.domain ?? null}
                  color={brand?.color ?? 'var(--color-blue-600)'}
                  className="shrink-0"
                />
                <div className="flex flex-col gap-3">
                  <span className="text-base font-medium text-foreground">{row.advertiser}</span>
                  <p className="text-pretty text-body-md text-foreground-alt">{row.persona}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
